import { createContext, useContext, useState, useCallback, useRef } from 'react'

const MediaContext = createContext(null)

export function MediaProvider({ children }) {
  const [localTracks, setLocalTracks] = useState([])
  const [remoteTracks, setRemoteTracks] = useState([])
  const [isVideoEnabled, setIsVideoEnabled] = useState(true)
  const [isAudioEnabled, setIsAudioEnabled] = useState(true)
  const [remoteAudioMuted, setRemoteAudioMuted] = useState(false)
  const [permissionError, setPermissionError] = useState(null)

  const localTracksRef = useRef([])
  const remoteTracksRef = useRef([])

  /**
   * Set local tracks (from Twilio)
   */
  const setLocalTracksFromTwilio = useCallback((tracks) => {
    setLocalTracks(tracks)
    localTracksRef.current = tracks
    setPermissionError(null)
  }, [])

  /**
   * Set remote tracks (from Twilio)
   */
  const setRemoteTracksFromTwilio = useCallback((tracks) => {
    setRemoteTracks(tracks)
    remoteTracksRef.current = tracks
  }, [])

  /**
   * Toggle video
   */
  const toggleVideo = useCallback(() => {
    const videoTrack = localTracksRef.current.find(track => track.kind === 'video')
    if (videoTrack) {
      videoTrack.enable(!videoTrack.isEnabled)
      setIsVideoEnabled(videoTrack.isEnabled)
    }
  }, [])

  /**
   * Toggle audio
   */
  const toggleAudio = useCallback(() => {
    const audioTrack = localTracksRef.current.find(track => track.kind === 'audio')
    if (audioTrack) {
      audioTrack.enable(!audioTrack.isEnabled)
      setIsAudioEnabled(audioTrack.isEnabled)
    }
  }, [])

  /**
   * Stop local tracks
   */
  const stopLocalTracks = useCallback(() => {
    if (localTracksRef.current) {
      localTracksRef.current.forEach(track => track.stop())
      setLocalTracks([])
      localTracksRef.current = []
    }
  }, [])

  /**
   * Stop remote tracks
   */
  const stopRemoteTracks = useCallback(() => {
    if (remoteTracksRef.current) {
      remoteTracksRef.current.forEach(track => track.stop())
      setRemoteTracks([])
      remoteTracksRef.current = []
    }
  }, [])

  /**
   * Stop all tracks
   */
  const stopAllTracks = useCallback(() => {
    stopLocalTracks()
    stopRemoteTracks()
  }, [stopLocalTracks, stopRemoteTracks])

  /**
   * Get local video track
   */
  const getLocalVideoTrack = useCallback(() => {
    return localTracksRef.current.find(track => track.kind === 'video')
  }, [])

  /**
   * Get local audio track
   */
  const getLocalAudioTrack = useCallback(() => {
    return localTracksRef.current.find(track => track.kind === 'audio')
  }, [])

  /**
   * Get remote video track
   */
  const getRemoteVideoTrack = useCallback(() => {
    return remoteTracksRef.current.find(track => track.kind === 'video')
  }, [])

  /**
   * Get remote audio track
   */
  const getRemoteAudioTrack = useCallback(() => {
    return remoteTracksRef.current.find(track => track.kind === 'audio')
  }, [])

  const value = {
    // Tracks
    localTracks,
    remoteTracks,
    
    // State
    isVideoEnabled,
    isAudioEnabled,
    remoteAudioMuted,
    permissionError,
    
    // Setters
    setRemoteAudioMuted,
    setLocalTracksFromTwilio,
    setRemoteTracksFromTwilio,
    
    // Controls
    toggleVideo,
    toggleAudio,
    
    // Track management
    stopLocalTracks,
    stopRemoteTracks,
    stopAllTracks,
    
    // Track getters
    getLocalVideoTrack,
    getLocalAudioTrack,
    getRemoteVideoTrack,
    getRemoteAudioTrack,
  }

  return <MediaContext.Provider value={value}>{children}</MediaContext.Provider>
}

export function useMedia() {
  const context = useContext(MediaContext)
  if (!context) {
    throw new Error('useMedia must be used within MediaProvider')
  }
  return context
}
